import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { hasBillingRole } from '../utils/roles';

const RequestCostsManager = ({ request, isAdmin, onInvoiceCreated }) => {
  // Invoice creation is billing-role gated on the backend (RF15) -- the
  // hardcoded isAdmin prop showed warehouse users editable controls
  // that 403 on submit. And a request with a live (non-cancelled)
  // invoice shows that invoice instead of a form that can only produce
  // duplicate errors.
  const { user } = useAuth();
  const canMutateBilling = hasBillingRole(user);
  const connectedInvoice = (request?.Invoices || []).find((inv) => inv.status !== 'cancelled') || null;
  const [costs, setCosts] = useState([]);
  const [newCost, setNewCost] = useState({
    description: '',
    feeType: 'labour',
    quantity: 1,
    unitPrice: 0,
    totalPrice: 0
  });
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // RF9: prefill from the customer's CONFIGURED rates instead of
  // hardcoded template dollars. Under the simplified catalog, manual
  // invoices are for Additional Labour (and ad-hoc lines ops types in);
  // handling/storage bill automatically. The labour rate comes from the
  // customer's active fee row -- rate per hour, quantity = hours worked.
  const [labourRate, setLabourRate] = useState(null);

  useEffect(() => {
    if (!request?.id) return;
    let cancelled = false;
    // Server-side resolution: the backend resolves the labour rate for
    // THIS request's warehouse with billing's canonical fee precedence
    // -- picking any company-wide row could bill a multi-warehouse
    // customer at the wrong warehouse's hourly rate.
    adminAPI.getManualBillingRates(request.id)
      .then((data) => {
        if (cancelled) return;
        setLabourRate(data?.labourRate ?? null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [request?.id]);

  const getCostTemplates = () => {
    if (labourRate === null) return [];
    return [{
      description: 'Additional Labour (hours)',
      feeType: 'labour',
      quantity: 1,
      unitPrice: labourRate,
      totalPrice: labourRate
    }];
  };

  // Initialize costs when request data changes
  useEffect(() => {
    if (request && request.id) {
      const templates = getCostTemplates();
      setCosts(templates);
    }
    // labourRate loads async; re-seed the prefill when it arrives.
  }, [request, labourRate]);
  
  // Calculate new cost total price
  const calculateTotalPrice = () => {
    return (newCost.quantity * newCost.unitPrice).toFixed(2);
  };
  
  // Update newCost when quantity, price, or type changes. Labour rows
  // are pinned to the SERVER-resolved rate -- the controller ignores
  // submitted labour rates, so an editable price would display totals
  // the created invoice will not match.
  useEffect(() => {
    const pinnedPrice = newCost.feeType === 'labour' && labourRate !== null ? labourRate : newCost.unitPrice;
    setNewCost((prev) => ({
      ...prev,
      unitPrice: pinnedPrice,
      totalPrice: (prev.quantity * pinnedPrice).toFixed(2)
    }));
  }, [newCost.quantity, newCost.unitPrice, newCost.feeType, labourRate]);
  
  // Handle adding a cost item
  const handleAddCost = () => {
    if (!newCost.description || newCost.quantity <= 0 || newCost.unitPrice <= 0) {
      setError('Please fill in all cost details correctly');
      return;
    }
    
    setCosts([
      ...costs,
      { ...newCost, totalPrice: parseFloat(calculateTotalPrice()) }
    ]);
    
    // Full reset INCLUDING feeType: omitting it left the selector
    // undefined after the first add -- the price input enabled (the
    // disable check only recognizes explicit 'labour') while submit's
    // fallback still sent the row as labour, so the displayed price
    // diverged from the server-billed rate.
    setNewCost({
      description: '',
      feeType: 'labour',
      quantity: 1,
      unitPrice: labourRate ?? 0,
      totalPrice: labourRate ?? 0
    });
    
    setError(null);
  };
  
  // Handle removing a cost item
  const handleRemoveCost = (index) => {
    const updatedCosts = [...costs];
    updatedCosts.splice(index, 1);
    setCosts(updatedCosts);
  };
  
  // Calculate total invoice amount
  const calculateInvoiceTotal = () => {
    return costs.reduce((total, cost) => total + parseFloat(cost.totalPrice), 0).toFixed(2);
  };
  
  // Generate invoice from costs
  const handleCreateInvoice = async () => {
    try {
      setIsCreatingInvoice(true);
      setError(null);
      
      // Format the costs into invoice line items
      // Shared invoice-item contract (RF9): rate + quantity; the
      // backend computes amounts and the tax line server-side. The old
      // unitPrice/totalPrice fields were never read by the backend,
      // which multiplied undefined and persisted NaN totals.
      const invoiceData = {
        requestId: request.id,
        items: costs.map(cost => ({
          description: cost.description,
          // labour rates are server-authoritative; special_project lines
          // keep their custom price.
          fee_type: cost.feeType === 'special_project' ? 'special_project' : 'labour',
          rate: parseFloat(cost.unitPrice),
          quantity: parseFloat(cost.quantity)
        }))
      };
      
      // Call the API to create the invoice
      // Creation lives on the ADMIN router only (RF15-gated);
      // /invoices has no create route.
      const response = await adminAPI.createInvoice(invoiceData);
      
      setSuccess('Invoice created successfully');
      
      // Call the callback with the invoice data
      if (onInvoiceCreated) {
        onInvoiceCreated(response.data);
      }
    } catch (err) {
      console.error('Failed to create invoice:', err);
      setError(err.response?.data?.message || 'Failed to create invoice');
    } finally {
      setIsCreatingInvoice(false);
    }
  };
  
  // Only admin users can edit costs and create invoices
  if (!isAdmin) {
    return null;
  }
  
  if (connectedInvoice) {
    return (
      <div className="text-sm text-gray-700">
        <p className="mb-1">This request is already invoiced.</p>
        <a href={`/admin/invoices/${connectedInvoice.id}`} className="text-indigo-600 hover:text-indigo-800 underline">
          Invoice {connectedInvoice.invoiceNumber || connectedInvoice.id?.substring(0, 8)}
        </a>
        <span className="ml-2 text-xs text-gray-500">({connectedInvoice.status})</span>
      </div>
    );
  }

  if (!canMutateBilling) {
    return (
      <p className="text-sm text-gray-500">
        Manual invoicing requires billing privileges (admin, manager, or accounting).
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <h3 className="text-lg font-medium text-gray-900">Service Costs</h3>
        <p className="mt-1 text-sm text-gray-500">
          Add or modify service costs for this request
        </p>
      </div>
      
      {/* Costs Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Quantity
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Unit Price
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total
              </th>
              <th scope="col" className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {costs.map((cost, index) => (
              <tr key={index}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {cost.description}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {cost.quantity}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  ${cost.unitPrice.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  ${cost.totalPrice.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    type="button"
                    onClick={() => handleRemoveCost(index)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
            
            {/* Add New Cost Row */}
            <tr className="bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex gap-2">
                  <select
                    value={newCost.feeType}
                    onChange={(e) => setNewCost({...newCost, feeType: e.target.value})}
                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                    title="Labour bills at the configured hourly rate; Special Project allows custom pricing"
                  >
                    <option value="labour">Labour</option>
                    <option value="special_project">Special Project</option>
                  </select>
                  <input
                    type="text"
                    value={newCost.description}
                    onChange={(e) => setNewCost({...newCost, description: e.target.value})}
                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Cost description"
                  />
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <input
                  type="number"
                  min="1"
                  value={newCost.quantity}
                  onChange={(e) => setNewCost({...newCost, quantity: parseFloat(e.target.value) || 0})}
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newCost.unitPrice}
                    onChange={(e) => setNewCost({...newCost, unitPrice: parseFloat(e.target.value) || 0})}
                    disabled={newCost.feeType === 'labour'}
                    title={newCost.feeType === 'labour' ? 'Labour bills at the configured hourly rate' : undefined}
                    className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 sm:text-sm border-gray-300 rounded-md disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                ${calculateTotalPrice()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <button
                  type="button"
                  onClick={handleAddCost}
                  className="text-indigo-600 hover:text-indigo-900"
                  disabled={!newCost.description || newCost.quantity <= 0 || newCost.unitPrice <= 0}
                >
                  Add
                </button>
              </td>
            </tr>
            
            {/* Total Row */}
            <tr className="bg-gray-100">
              <td colSpan="3" className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                Total:
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                ${calculateInvoiceTotal()}
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
      
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      {success && (
        <div className="rounded-md bg-green-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">{success}</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleCreateInvoice}
          disabled={isCreatingInvoice || costs.length === 0}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
        >
          {isCreatingInvoice ? 'Creating...' : 'Create Invoice'}
        </button>
      </div>
    </div>
  );
};

export default RequestCostsManager; 